import React, { useState, useEffect, useCallback } from 'react';
import { cssUrl } from '../utils/cssVersion';
import TimeKeeping from '../components/TimeKeeping';
import IgluDashboard from '../components/IgluDashboard';
import SupervisorDashboard from '../components/SupervisorDashboard';
import PeriodicalDashboard from '../components/PeriodicalDashboard';
import TestNTagDashboard from '../components/TestNTagDashboard';

/* Reuse the FilterTemplate stylesheet (same one HigherView / SubbiePayments load)
 * so the fr-tab-* classes are available. */
const filterTemplateStyles = cssUrl('filterTemplate.css');

/* -------------------------------------------------------------------------
 * Dashboard — five tabs (mirrors HigherView's nav):
 *   Iglu         -> <IgluDashboard />        (South Yarra cleaning)
 *   Periodical   -> <PeriodicalDashboard />  (placeholder until designed)
 *   TimeKeeping  -> <TimeKeeping />
 *   Supervisors  -> <SupervisorDashboard />
 *   Test & Tag   -> <TestNTagDashboard />    (placeholder until designed)
 *
 * Every component is rendered "inline" (no onClose prop) so it draws its own
 * shell — title, help, month nav, etc. — rather than a modal; the header here
 * only carries the tab bar. Each tab is mounted on first visit and then kept
 * mounted (hidden) so month / filter / search state survives tab switches.
 * ---------------------------------------------------------------------- */

const TABS = [
  { key: 'iglu',        label: 'Iglu',        Component: IgluDashboard },
  { key: 'periodical',  label: 'Periodical',  Component: PeriodicalDashboard },
  { key: 'timekeeping', label: 'TimeKeeping', Component: TimeKeeping },
  { key: 'supervisors', label: 'Supervisors', Component: SupervisorDashboard },
  { key: 'testntag',    label: 'Test & Tag',  Component: TestNTagDashboard },
];

/* ---------- inline style tokens (same as HigherView) ---------- */
const S = {
  panel: {
    position: 'relative', overflow: 'hidden',
    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
    border: '1px solid rgba(34, 211, 238, 0.18)', borderRadius: 16, padding: 16,
  },
  panelAccent: {
    position: 'absolute', top: 0, left: 0, right: 0, height: 3,
    background: 'linear-gradient(90deg, #22d3ee, #3b82f6, #8b5cf6)',
  },
  title: { margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#f1f5f9' },
  /* same ↻ button as IgluDashboard's header */
  navBtn: { width: 32, height: 32, borderRadius: 9, border: '1px solid rgba(34,211,238,0.4)',
    background: 'rgba(34,211,238,0.1)', color: '#22d3ee', cursor: 'pointer', fontSize: '0.95rem', lineHeight: 1 },
};

const Spin = () => (
  <span style={{ width: 12, height: 12, border: '2px solid currentColor', borderTopColor: 'transparent',
    borderRadius: '50%', display: 'inline-block', animation: 'db-spin 0.7s linear infinite' }} />
);

const Dashboard = ({ initialTab = 'iglu' }) => {
  const [activeTab, setActiveTab] = useState(
    TABS.some((t) => t.key === initialTab) ? initialTab : TABS[0].key
  );
  /* tabs that have been opened at least once — these stay mounted */
  const [visited, setVisited] = useState(() => new Set([activeTab]));
  const [refreshKey, setRefreshKey] = useState(0);        // bump -> every tab refetches
  const [busy, setBusy] = useState({});                   // { tabKey: true } while fetching
  const anyBusy = Object.values(busy).some(Boolean);

  const onBusy = useCallback((key, isBusy) =>
    setBusy((b) => (b[key] === isBusy ? b : { ...b, [key]: isBusy })), []);
  const refreshAll = () => { if (!anyBusy) setRefreshKey((k) => k + 1); };

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = filterTemplateStyles;
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  const selectTab = (key) => {
    setActiveTab(key);
    setVisited((v) => (v.has(key) ? v : new Set([...v, key])));
  };

  const activeIndex = TABS.findIndex((t) => t.key === activeTab);
  const pct = 100 / TABS.length;

  return (
    <div style={{ width: '100%', maxWidth: '100%' }}>
      {/* header + tabs */}
      <div style={{ ...S.panel, marginBottom: 16 }}>
        <div style={S.panelAccent} />
        <style>{`@keyframes db-spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
          <h3 style={S.title}>Dashboards</h3>
          <button type="button" style={{ ...S.navBtn, marginLeft: 'auto', opacity: anyBusy ? 0.6 : 1 }}
            title="Refresh all dashboards — keeps current filters" onClick={refreshAll}>
            {anyBusy ? <Spin /> : '↻'}
          </button>
        </div>

        <div className="fr-tab-bar" style={{ background: 'transparent', borderBottom: 'none' }}>
          <div className="fr-tab-list">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`fr-tab-item${activeTab === t.key ? ' fr-tab-active' : ''}`}
                onClick={() => selectTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="fr-tab-track">
            <div className="fr-tab-indicator" style={{ left: `${activeIndex * pct}%`, width: `${pct}%` }} />
          </div>
        </div>
      </div>

      {/* panels — mounted on first visit, hidden (not unmounted) afterwards */}
      {TABS.map(({ key, Component }) => {
        if (!visited.has(key)) return null;
        return (
          <div key={key} style={{ display: activeTab === key ? 'block' : 'none' }}>
            <Component refreshKey={refreshKey} onBusy={(b) => onBusy(key, b)} />
          </div>
        );
      })}
    </div>
  );
};

export default Dashboard;